import { useState, useCallback } from 'react';
import { DEFAULT_FORM_DATA, WIZARD_STEPS } from '../constants/defaults';
import { validateStep, hasErrors } from '../utils/validation';

export default function useWizard(initialData = DEFAULT_FORM_DATA) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({});

  const totalSteps = WIZARD_STEPS.length;

  const updateFormData = useCallback((section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const updateSection = useCallback((section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data
      }
    }));
  }, []);

  const validateCurrentStep = useCallback(() => {
    const stepErrors = validateStep(currentStep, formData);
    setErrors(stepErrors);
    return !hasErrors(stepErrors);
  }, [currentStep, formData]);

  const nextStep = useCallback(() => {
    if (validateCurrentStep()) {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(prev => prev + 1);
        setErrors({});
        return true;
      }
    }
    return false;
  }, [currentStep, totalSteps, validateCurrentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setErrors({});
    }
  }, [currentStep]);

  const goToStep = useCallback((step) => {
    if (step >= 0 && step < totalSteps) {
      // Only allow going back or to current step without validation
      if (step <= currentStep) {
        setCurrentStep(step);
        setErrors({});
      } else {
        // Validate all steps up to target step
        let canProceed = true;
        for (let i = currentStep; i < step; i++) {
          const stepErrors = validateStep(i, formData);
          if (hasErrors(stepErrors)) {
            canProceed = false;
            setErrors(stepErrors);
            break;
          }
        }
        if (canProceed) {
          setCurrentStep(step);
          setErrors({});
        }
      }
    }
  }, [currentStep, totalSteps, formData]);

  const markTouched = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, []);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return {
    currentStep,
    formData,
    errors,
    isSubmitting,
    touched,
    totalSteps,
    isFirstStep,
    isLastStep,
    progress,
    currentStepInfo: WIZARD_STEPS[currentStep],
    setFormData,
    updateFormData,
    updateSection,
    validateCurrentStep,
    nextStep,
    prevStep,
    goToStep,
    markTouched,
    setIsSubmitting,
    setErrors,
    reset
  };
}
